import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMemberAuth } from "../context/MemberAuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { CREST_URL } from "../components/SiteLayout";
import { toast } from "sonner";
import { Search } from "lucide-react";

const MemberLoginPage = () => {
  const { login } = useMemberAuth();
  const nav = useNavigate();
  const [roster, setRoster] = useState([]);
  const [q, setQ] = useState("");
  const [editionFilter, setEditionFilter] = useState("all");
  const [selected, setSelected] = useState(null); // member obj
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/members/roster")
      .then((res) => setRoster(res.data))
      .catch(() => toast.error("Could not load member roster"))
      .finally(() => setLoading(false));
  }, []);

  const editions = useMemo(() => {
    const s = new Set(roster.map((m) => m.edition));
    return ["all", ...Array.from(s).sort().reverse()];
  }, [roster]);

  const filtered = useMemo(() => {
    return roster.filter((m) => {
      if (editionFilter !== "all" && m.edition !== editionFilter) return false;
      if (!q) return true;
      return m.name.toLowerCase().includes(q.toLowerCase());
    });
  }, [roster, q, editionFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      setErr("Please select your name from the list.");
      return;
    }
    if (pin.length !== 4) {
      setErr("Please enter your 4-digit PIN.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      await login(selected.id, pin);
      toast.success(`Welcome, ${selected.name.split(" ")[0]}.`);
      nav("/member-portal");
    } catch (error) {
      const msg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="member-login-page">
      <div className="hidden lg:flex hero-bg relative items-center justify-center">
        <div className="text-center text-white px-10">
          <img src={CREST_URL} alt="GODSC" className="h-36 w-auto mx-auto" />
          <div className="overline text-[#D4AF37] mt-6">Members Portal</div>
          <h2 className="font-serif-display text-5xl mt-3">For the Brotherhood.</h2>
          <p className="mt-5 max-w-md mx-auto text-[#D8D3C4] text-sm leading-relaxed">
            Pay your annual dues, view your status, and stay current with the
            Club. Use the 4-digit PIN given to you by the Executive Board.
          </p>
        </div>
      </div>

      <div className="flex items-start lg:items-center justify-center px-6 py-12 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          <Link to="/" className="overline text-[#5C5C5C] hover:text-[#C9A227]" data-testid="back-home">
            &larr; Back to Site
          </Link>
          <h1 className="font-serif-display text-4xl text-[#0A1F44] mt-6">Member Sign In</h1>
          <div className="h-[2px] w-12 bg-[#D4AF37] mt-4 mb-6" />

          <form onSubmit={submit} className="space-y-5" data-testid="member-login-form">
            {/* Edition filter */}
            <div>
              <label className="overline text-[#5C5C5C] block mb-2">Filter by Edition</label>
              <div className="flex flex-wrap gap-2">
                {editions.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setEditionFilter(e)}
                    className={`px-3 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                      editionFilter === e
                        ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                        : "bg-white text-[#0A1F44] border-[#D8D3C4] hover:border-[#0A1F44]"
                    }`}
                    data-testid={`edition-filter-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            {/* Name search + list */}
            <div>
              <label className="overline text-[#5C5C5C] block mb-2">Find Your Name</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                <input
                  placeholder="Start typing your name..."
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setSelected(null); }}
                  className="pl-9 pr-3 py-3 w-full border border-[#D8D3C4] bg-white text-sm focus:outline-none focus:border-[#D4AF37] rounded-none"
                  data-testid="member-name-search"
                />
              </div>
              {loading ? (
                <div className="text-sm text-[#5C5C5C] mt-3 italic">Loading roster...</div>
              ) : (
                <div className="border border-[#D8D3C4] border-t-0 max-h-[220px] overflow-y-auto mt-0" data-testid="member-list">
                  {filtered.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-[#5C5C5C] italic">No matches</div>
                  ) : (
                    filtered.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelected(m)}
                        className={`w-full text-left px-4 py-2.5 text-sm border-b border-[#D8D3C4] last:border-b-0 ${
                          selected?.id === m.id
                            ? "bg-[#0A1F44] text-white"
                            : "hover:bg-[#F5F1E6] text-[#0A1F44]"
                        }`}
                        data-testid={`member-option-${m.id}`}
                      >
                        <span className="font-medium">{m.name}</span>
                        <span className={`text-[10px] tracking-[0.22em] uppercase ml-2 ${selected?.id === m.id ? "text-[#D4AF37]" : "text-[#C9A227]"}`}>
                          {m.edition}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected confirmation */}
            {selected && (
              <div className="bg-[#F5F1E6] border border-[#D4AF37] px-4 py-3 text-sm" data-testid="member-selected">
                Selected: <strong>{selected.name}</strong>{" "}
                <span className="text-[#C9A227] text-[11px] tracking-[0.2em] uppercase">
                  &middot; {selected.edition} Edition
                </span>
              </div>
            )}

            {/* PIN */}
            <div>
              <label className="overline text-[#5C5C5C] block mb-2">4-Digit PIN</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                pattern="[0-9]{4}"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="w-full border border-[#D8D3C4] px-3 py-3 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 rounded-none text-lg tracking-[0.6em] text-center font-serif-display"
                data-testid="member-pin"
              />
            </div>

            {err && (
              <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="member-login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary-navy w-full disabled:opacity-60"
              data-testid="member-login-submit"
            >
              {submitting ? "Signing in..." : "Sign In"}
            </button>

            <p className="text-xs text-[#5C5C5C] leading-relaxed">
              Don&rsquo;t know your PIN? Reach out to the Treasurer or any member
              of the Executive Board.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberLoginPage;
